import type { PlatformStats } from "@/lib/types/stats";
import { publicLanguages } from "@/lib/language-name";
import type { LeaderboardEntry } from "@/components/leaderboard/leaderboard.types";
import { publicName } from "@/components/leaderboard/leaderboard-ranking";

// Pure helpers of the public home page: everything here is testable without
// rendering. One definition per figure, so the hero counter, the figures
// section and the map never disagree.

/** The locale of the page language, never the browser's. */
export function pageLocale(language: string): string {
  return language === "en" ? "en-US" : "fr-FR";
}

/** 12345 -> "12 345" in French, "12,345" in English. */
export function formatCount(value: number, language: string): string {
  return new Intl.NumberFormat(pageLocale(language), { maximumFractionDigits: 0 }).format(Math.round(value));
}

/** Hours with one decimal under 100, whole hours above: "12,5" / "1 234". */
export function formatHours(value: number, language: string): string {
  const digits = value < 100 ? 1 : 0;
  return new Intl.NumberFormat(pageLocale(language), {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(value);
}

/** 0.4567 -> "46 %" in French, "46%" in English. */
export function formatShare(ratio: number, language: string): string {
  return new Intl.NumberFormat(pageLocale(language), { style: "percent", maximumFractionDigits: 0 }).format(ratio);
}

export interface MapCountry {
  name: string;
  value: number;
}

export interface CollectionCountries {
  /** "collection": where contributions were recorded; "profile": profile country. */
  source: "collection" | "profile";
  countries: MapCountry[];
}

/**
 * The countries the map paints and the "countries" figure counts: the real
 * collection places when the API sends them, the profile countries otherwise.
 */
export function collectionCountries(stats: PlatformStats): CollectionCountries {
  const clean = (rows: { name: string; value: number }[]) =>
    rows.filter((row) => row.name && row.name.trim() && row.value > 0).map((row) => ({ name: row.name, value: row.value }));
  const collected = clean(stats.collection?.byCountry ?? []);
  if (collected.length > 0) return { source: "collection", countries: collected };
  return { source: "profile", countries: clean(stats.contributors.byCountry ?? []) };
}

export type FigureKey = "recordings" | "hours" | "contributors" | "languages" | "countries";

export interface PlatformFigure {
  key: FigureKey;
  value: number;
  /** Hours keep one decimal; the rest are whole counts. */
  kind: "count" | "hours";
}

/** The five figures of the page, in display order. */
export function platformFigures(stats: PlatformStats): PlatformFigure[] {
  return [
    { key: "recordings", value: stats.audio.total, kind: "count" },
    { key: "hours", value: stats.audio.recordedHours, kind: "hours" },
    { key: "contributors", value: stats.contributors.total, kind: "count" },
    { key: "languages", value: publicLanguages(stats.languages.byLanguage).length, kind: "count" },
    { key: "countries", value: collectionCountries(stats).countries.length, kind: "count" },
  ];
}

export interface LanguageTile {
  code: string;
  name: string;
  total: number;
  accepted: number;
  /** Validated share of what was collected, between 0 and 1. */
  validatedShare: number;
}

/**
 * Language tiles, biggest collection first, ties by name. Display names from
 * /langues win over the platform's when they exist (the local spelling).
 */
export function languageTiles(
  byLanguage: PlatformStats["languages"]["byLanguage"],
  displayNames: Record<string, string> = {},
  language = "fr",
): LanguageTile[] {
  return publicLanguages(byLanguage, displayNames, language)
    .map((row) => ({
      ...row,
      validatedShare: row.total > 0 ? Math.min(1, row.accepted / row.total) : 0,
    }))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, pageLocale(language)));
}

/** Names of the languages for the hero pills, the most collected first. */
export function heroLanguageNames(
  byLanguage: PlatformStats["languages"]["byLanguage"],
  displayNames: Record<string, string> = {},
  language = "fr",
  max = 8,
): string[] {
  return languageTiles(byLanguage, displayNames, language)
    .slice(0, max)
    .map((tile) => tile.name);
}

/** Lower-cased code -> display name, from /langues. */
export function displayNameMap(active: { code: string; displayName?: string; name?: string }[]): Record<string, string> {
  return Object.fromEntries(
    active
      .filter((lang) => lang.code)
      .map((lang) => [lang.code.toLowerCase(), (lang.displayName || lang.name || "").trim()])
      .filter(([, name]) => name),
  );
}

const EMAIL = /[^\s@]+@[^\s@]+/;

/**
 * The name the public board shows. publicName already strips an email shown
 * as a display name; a username that is itself an email is cut too, so an
 * address never reaches a visitor.
 */
export function safePublicName(entry: Pick<LeaderboardEntry, "displayName" | "username">): string {
  const name = publicName(entry);
  return EMAIL.test(name) ? name.split("@")[0] : name;
}

export interface LeaderRow {
  rank: number;
  name: string;
  initial: string;
  contributions: number;
}

export function leaderRows(entries: LeaderboardEntry[], max = 5): LeaderRow[] {
  return entries.slice(0, max).map((entry) => {
    const name = safePublicName(entry);
    return { rank: entry.rank, name, initial: name.charAt(0).toUpperCase() || "?", contributions: entry.contributions };
  });
}

/** The part of a FAQ entry the home page renders. */
export interface HomeFaqItem {
  id: number;
  question: string;
  answer: string;
  orderIndex: number;
  isPublished: boolean;
}

/** Published questions, in the admin's order, at most `max`. */
export function homeFaq(items: HomeFaqItem[] | undefined, max = 6): HomeFaqItem[] {
  return (items ?? [])
    .filter((item) => item.isPublished !== false && item.question?.trim() && item.answer?.trim())
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
    .slice(0, max);
}
